// Cycle-16 mention-picker popover. Surfaces a list of `MentionCandidate`
// rows when the user types `@` in the composer. Pure presentational — the
// host owns the candidate array + filtering, and selection state is
// driven by the parent (keyboard arrow handling lives there too).
//
// Visual reference: Figma `2918:10419` (the "Mention" section). Each row
// mirrors the `tag-you` frame (`2918:10420`):
//   - row size 297×58, paddings L12 R12 T6 B6, itemSpacing 8
//   - avatar 32×32 circular
//   - name Lato 400 14/19.6 → secondary text
//   - subtitle / time Lato 400 10/14 → tertiary text
//   - body / display-name Lato 400 16/22.4 → primary text
// Hover/active row tint is the brand indigo at 12% (no explicit Figma
// node — derived from other tap states in the design library).

import React, { useCallback, useMemo } from 'react';
import { Image, Pressable, StyleSheet, Text, type TextStyle, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import type { MentionCandidate } from '../../chat/ChatRoom.types';

export interface MentionPickerProps {
  /** Whether the popover should render. The parent controls visibility. */
  readonly open: boolean;
  /** Filtered, host-supplied list of candidates (already capped at 8). */
  readonly candidates: ReadonlyArray<MentionCandidate>;
  /** The substring after `@`. Surfaced for empty-state copy + tests. */
  readonly query: string;
  /** Index of the currently highlighted row. Drives `selected` styling. */
  readonly selectedIndex: number;
  /** Fired when the user clicks a row OR presses Enter on the highlighted one. */
  readonly onSelect: (candidate: MentionCandidate) => void;
  /** Fired when the picker should dismiss (Esc, blur, outside-click). */
  readonly onClose: () => void;
  /**
   * Optional anchor coordinates used by hosts to position the popover.
   * Pure presentational — the component itself just renders inline and
   * lets the host wrap it in a `Pressable` / portal if needed. Kept on
   * the prop surface so iOS / macOS hosts can share the contract.
   */
  readonly anchorRect?:
    | { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
    | undefined;
  readonly testID?: string | undefined;
}

const ROW_HEIGHT = 58;
const PANEL_MAX_HEIGHT = 8 * ROW_HEIGHT;

export const MentionPicker = React.memo<MentionPickerProps>(
  ({ open, candidates, query, selectedIndex, onSelect, onClose: _onClose, anchorRect, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // Surface color — composer pill in Figma is `#222429` (dark) /
    // `#F5F5F7` (light, `surface.secondary`). The popover sits ON TOP of
    // the composer, so we use `surface.primary` for separation.
    const panelStyle = useMemo<ViewStyle[]>(() => {
      const base: ViewStyle[] = [
        styles.panel,
        {
          backgroundColor: colors.surface.primary,
          borderColor: colors.border.secondary,
        },
      ];
      if (anchorRect !== undefined) {
        base.push({ width: anchorRect.width });
      }
      return base;
    }, [colors.surface.primary, colors.border.secondary, anchorRect]);

    if (!open) {
      return null;
    }

    return (
      <View
        accessibilityRole="menu"
        accessibilityLabel="Mention candidates"
        style={panelStyle}
        testID={testID ?? 'composer-mention-picker'}
      >
        {candidates.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={[styles.emptyText, { color: colors.text.secondary }]}>
              No matches{query.length > 0 ? ` for "${query}"` : ''}
            </Text>
          </View>
        ) : (
          candidates.map((candidate, index) => (
            <MentionRow
              key={candidate.userId}
              candidate={candidate}
              selected={index === selectedIndex}
              onSelect={onSelect}
              colorScheme={colorScheme}
            />
          ))
        )}
      </View>
    );
  },
);

MentionPicker.displayName = 'MentionPicker';

interface MentionRowProps {
  readonly candidate: MentionCandidate;
  readonly selected: boolean;
  readonly onSelect: (candidate: MentionCandidate) => void;
  readonly colorScheme: 'light' | 'dark';
}

const MentionRow: React.FC<MentionRowProps> = ({
  candidate,
  selected,
  onSelect,
  colorScheme,
}) => {
  const colors = tokens.color.semantic[colorScheme];
  const handlePress = useCallback(() => {
    onSelect(candidate);
  }, [candidate, onSelect]);

  // Brand indigo (#537EFF) @ 12% in both schemes — matches other tap surfaces.
  const hoverFill = 'rgba(83,126,255,0.12)';

  const rowStyle: ViewStyle[] = selected
    ? [styles.row, { backgroundColor: hoverFill } as ViewStyle]
    : [styles.row];

  const nameStyle: TextStyle = {
    color: selected ? colors.accent.indigo : colors.text.primary,
    fontFamily: tokens.typography.fontFamily.base,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
  };
  const subtitleStyle: TextStyle = {
    color: colors.text.secondary,
    fontFamily: tokens.typography.fontFamily.base,
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
  };

  // Avatar 32×32 circular (Figma `2918:10422` Profile image variant Small).
  // Falls back to initials block when no `avatarUri` is set.
  const initials = useMemo(() => {
    const parts = candidate.displayName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return `${first}${last}`.toUpperCase();
  }, [candidate.displayName]);

  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityLabel={`Mention ${candidate.displayName}`}
      accessibilityState={{ selected }}
      onPress={handlePress}
      style={rowStyle}
      testID={`composer-mention-row-${candidate.userId}`}
    >
      <View
        style={[styles.avatar, { backgroundColor: colors.surface.tertiary }]}
      >
        {candidate.avatarUri !== undefined && candidate.avatarUri.length > 0 ? (
          <Image source={{ uri: candidate.avatarUri }} style={styles.avatarImage} />
        ) : (
          <Text
            style={[
              styles.avatarInitials,
              { color: colors.text.secondary },
            ]}
          >
            {initials || '?'}
          </Text>
        )}
      </View>
      <View style={styles.textColumn}>
        <Text numberOfLines={1} style={nameStyle}>
          {candidate.displayName}
        </Text>
        {candidate.subtitle !== undefined && candidate.subtitle.length > 0 ? (
          <Text numberOfLines={1} style={subtitleStyle}>
            {candidate.subtitle}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  // Floating panel: rounded card with hairline stroke and a soft shadow
  // so it reads as elevated over the composer. Figma doesn't model the
  // popover container directly, so the radii + shadow follow the design
  // library defaults (radius.lg = 20, shadow.md).
  panel: {
    borderRadius: tokens.radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: tokens.spacing.xs,
    maxHeight: PANEL_MAX_HEIGHT,
    // Web/macOS only — react-native ignores `boxShadow`. Kept as inline
    // string so react-native-web picks it up; native stacks fall back to
    // the hairline border.
    ...(Platform_isWeb()
      ? ({ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } as unknown as ViewStyle)
      : {}),
  } satisfies ViewStyle,
  row: {
    // Figma `tag-you` (2918:10420): paddings L12 R12 T6 B6, gap 8.
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs + 2,
    borderRadius: tokens.radius.sm,
    marginHorizontal: tokens.spacing.xs,
  } satisfies ViewStyle,
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  } satisfies ViewStyle,
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatarInitials: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    fontWeight: '700',
  } satisfies TextStyle,
  textColumn: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
  } satisfies ViewStyle,
  emptyState: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  emptyText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 20,
  } satisfies TextStyle,
});

// Tiny helper to detect react-native-web without pulling in `Platform`
// (which native macOS resolves differently). `document` only exists in
// the web bundle, so this is a one-line type-safe sniff.
function Platform_isWeb(): boolean {
  return typeof document !== 'undefined';
}

// ---------------------------------------------------------------------------
// `useMentionPicker` — small hook that owns query parsing + filtering +
// keyboard nav. The composer (or any host) can adopt this incrementally
// without taking on a TipTap-style document refactor.
//
// Returns:
//   - `open`              : whether the picker should render
//   - `query`             : the substring after the most recent `@`
//   - `filteredCandidates`: case-insensitive substring match, capped at 8
//   - `selectedIndex`     : currently highlighted row
//   - `handleKeyDown`     : ArrowUp / ArrowDown / Enter / Escape handler
//   - `setSelectedIndex`  : explicit setter for hover-driven highlight
//   - `dismiss`           : closes the picker (clears query)
//   - `insertMention(c)`  : returns the next text after replacing the
//                          active `@token` with `@<displayName> ` so the
//                          caller can `setText` and reset.
//
// Limit: this hook works against a plain string. It does NOT mutate the
// editor's internal Slate / Tiptap document. The composer renders the
// inserted mention as `@Name ` text — visually identical to a
// `MentionPill` when the bubble side renders structured tokens via
// `messageFormatters.ts`.
// ---------------------------------------------------------------------------

const MAX_RESULTS = 8;
const QUERY_MAX_LEN = 16;

export interface UseMentionPickerResult {
  readonly open: boolean;
  readonly query: string;
  readonly filteredCandidates: ReadonlyArray<MentionCandidate>;
  readonly selectedIndex: number;
  readonly setSelectedIndex: (next: number) => void;
  readonly dismiss: () => void;
  readonly insertMention: (
    candidate: MentionCandidate,
    text: string,
    caret: number,
  ) => { readonly text: string; readonly caret: number };
}

interface MentionQueryHandle {
  readonly query: string;
  readonly anchorOffset: number; // index of the `@`
}

/**
 * Pure parser: given the current text + caret position, return the
 * pending `@token` (or `null` when none). Stops at whitespace or after
 * `QUERY_MAX_LEN` chars, matching the spec.
 */
export function parseMentionQuery(text: string, caret: number): MentionQueryHandle | null {
  if (caret <= 0 || caret > text.length) {
    return null;
  }
  // Walk backwards from the caret looking for the `@` anchor.
  let i = caret - 1;
  let len = 0;
  while (i >= 0 && len <= QUERY_MAX_LEN) {
    const ch = text[i];
    if (ch === '@') {
      // Require word boundary before `@` (start-of-string OR whitespace)
      // so emails don't trigger the picker.
      const before = i === 0 ? '' : text[i - 1] ?? '';
      if (i === 0 || /\s/.test(before)) {
        return { query: text.slice(i + 1, caret), anchorOffset: i };
      }
      return null;
    }
    if (ch === undefined || /\s/.test(ch)) {
      return null;
    }
    i -= 1;
    len += 1;
  }
  return null;
}

export function filterMentionCandidates(
  candidates: ReadonlyArray<MentionCandidate>,
  query: string,
): ReadonlyArray<MentionCandidate> {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return candidates.slice(0, MAX_RESULTS);
  }
  const matches: MentionCandidate[] = [];
  for (const c of candidates) {
    const name = c.displayName.toLowerCase();
    const sub = (c.subtitle ?? '').toLowerCase();
    if (name.includes(needle) || sub.includes(needle)) {
      matches.push(c);
      if (matches.length >= MAX_RESULTS) break;
    }
  }
  return matches;
}

/**
 * Hook wrapper that derives `{ open, query, filteredCandidates, ... }`
 * from the current editor text + caret. The composer wires the keyboard
 * handler returned by `handleKeyEvent` into its TextInput's
 * `onKeyPress`, and calls `insertMention` on row select.
 */
export function useMentionPicker(
  text: string,
  caret: number,
  candidates: ReadonlyArray<MentionCandidate>,
): UseMentionPickerResult & {
  readonly handleKeyEvent: (
    key: 'ArrowUp' | 'ArrowDown' | 'Enter' | 'Escape',
  ) => boolean;
} {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [dismissed, setDismissed] = React.useState(false);

  const queryHandle = useMemo(
    () => parseMentionQuery(text, caret),
    [text, caret],
  );
  const query = queryHandle?.query ?? '';

  const filteredCandidates = useMemo(
    () => (queryHandle === null ? [] : filterMentionCandidates(candidates, query)),
    [queryHandle, candidates, query],
  );

  const open = !dismissed && queryHandle !== null && candidates.length > 0;

  // Reset highlight + dismissed flag whenever the query changes.
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [query]);
  React.useEffect(() => {
    if (queryHandle === null) {
      setDismissed(false);
    }
  }, [queryHandle]);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const insertMention = useCallback(
    (
      candidate: MentionCandidate,
      currentText: string,
      currentCaret: number,
    ): { readonly text: string; readonly caret: number } => {
      const handle = parseMentionQuery(currentText, currentCaret);
      if (handle === null) {
        return { text: currentText, caret: currentCaret };
      }
      const before = currentText.slice(0, handle.anchorOffset);
      const after = currentText.slice(currentCaret);
      const insertion = `@${candidate.displayName} `;
      const nextText = `${before}${insertion}${after}`;
      const nextCaret = before.length + insertion.length;
      setDismissed(false);
      setSelectedIndex(0);
      return { text: nextText, caret: nextCaret };
    },
    [],
  );

  const handleKeyEvent = useCallback(
    (key: 'ArrowUp' | 'ArrowDown' | 'Enter' | 'Escape'): boolean => {
      if (!open) return false;
      switch (key) {
        case 'ArrowUp':
          setSelectedIndex((idx) =>
            filteredCandidates.length === 0
              ? 0
              : (idx - 1 + filteredCandidates.length) % filteredCandidates.length,
          );
          return true;
        case 'ArrowDown':
          setSelectedIndex((idx) =>
            filteredCandidates.length === 0 ? 0 : (idx + 1) % filteredCandidates.length,
          );
          return true;
        case 'Enter':
          // Caller resolves the selected candidate + calls `insertMention`.
          return true;
        case 'Escape':
          dismiss();
          return true;
      }
    },
    [open, filteredCandidates.length, dismiss],
  );

  return {
    open,
    query,
    filteredCandidates,
    selectedIndex,
    setSelectedIndex,
    dismiss,
    insertMention,
    handleKeyEvent,
  };
}
